import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, debounceTime, switchMap, EMPTY } from 'rxjs';
import { DomainContextService } from '../../services/domain-context';
import {
  ClustersOrganizeService,
  CompetitorInspirationData,
  CompetitorInspirationBucketRow,
  OrganizePillarDraft,
  OrganizePinDraft,
  OrganizePreviewData,
  OrganizeProposalMeta,
  OrganizePillarSimilarHint,
  OrganizeStatusData,
  OrganizeUrlAssignment
} from '../../services/clusters-organize.service';
import { DomainTopicsService } from '../../services/domain-topics';
import {
  TopicPickerOption,
  buildTopicOptionsFromParents,
  topicKindTagClass as topicKindTagClassForOption
} from '../../utils/topic-picker-options';
import { InventoryPlanClustersService } from '../../services/inventory-plan-clusters.service';
import { NotificationService } from '../../services/notification.service';

type ModalKind = 'none' | 'addPillar' | 'merge' | 'split' | 'apply' | 'start';

@Component({
  selector: 'app-clusters',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './clusters.html',
  styleUrl: './clusters.css'
})
export class ClustersComponent implements OnInit, OnDestroy {
  domain = '';
  loading = false;
  previewLoading = false;
  applying = false;
  error: string | null = null;

  status: OrganizeStatusData | null = null;
  preview: OrganizePreviewData | null = null;

  pillars: OrganizePillarDraft[] = [];
  pins: OrganizePinDraft[] = [];

  selectedPillarSlug: string | '__unassigned__' = '';
  selectedUrlId: string | null = null;
  urlSearch = '';

  modal: ModalKind = 'none';
  topicPickerOptions: TopicPickerOption[] = [];
  addPillarSearch = '';
  mergeSourceSlug = '';
  mergeTargetSlug = '';
  splitSourceSlug = '';
  splitMode: 'pathPrefix' | 'childSlug' | 'manual' = 'pathPrefix';
  splitPathPrefix = '';
  splitChildSlug = '';
  splitNewLabel = '';
  splitNewSlug = '';
  splitChildOptions: string[] = [];
  splitManualUrlIds: string[] = [];
  applyMode: 'replace' | 'merge' = 'replace';

  proposalMeta: OrganizeProposalMeta | null = null;
  competitorInspiration: CompetitorInspirationData | null = null;
  competitorInspirationLoading = false;
  competitorInspirationError: string | null = null;
  competitorViewMode: 'grouped' | 'flat' = 'grouped';
  expandedCompetitorBuckets = new Set<string>();

  private subs = new Subscription();
  private previewRequest$ = new Subject<void>();

  constructor(
    private domainContext: DomainContextService,
    private clustersOrganize: ClustersOrganizeService,
    private domainTopicsService: DomainTopicsService,
    private planClusters: InventoryPlanClustersService,
    private notifications: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.domainContext.ensureInitialized();
    this.subs.add(
      this.domainContext.selectedDomain$.subscribe((domain) => {
        this.domain = domain || '';
        this.resetWorkspace();
        if (this.domain) {
          this.loadStatus();
        }
      })
    );
    this.subs.add(
      this.previewRequest$
        .pipe(
          debounceTime(300),
          switchMap(() => {
            if (!this.domain || this.pillars.length === 0) {
              this.preview = null;
              this.previewLoading = false;
              return EMPTY;
            }
            this.previewLoading = true;
            return this.clustersOrganize.preview(
              this.domain,
              this.pillars.map((p) => ({ topicSlug: p.topicSlug, label: p.label })),
              this.pins
            );
          })
        )
        .subscribe({
          next: (res: any) => {
            this.previewLoading = false;
            if (res?.data) {
              this.preview = res.data;
              if (!this.selectedPillarSlug && this.preview && this.preview.pillars.length > 0) {
                this.selectedPillarSlug = this.preview.pillars[0].topicSlug;
              }
              this.loadCompetitorInspiration();
            }
          },
          error: (err) => {
            this.previewLoading = false;
            this.error = err?.error?.message || err?.message || 'Preview failed';
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get ready(): boolean {
    return Boolean(this.status?.ready);
  }

  get selectedPillarPreview() {
    if (!this.preview) return null;
    if (this.selectedPillarSlug === '__unassigned__') return null;
    return this.preview.pillars.find((p) => p.topicSlug === this.selectedPillarSlug) || null;
  }

  get visibleUrls(): OrganizeUrlAssignment[] {
    if (!this.preview) return [];
    const q = this.urlSearch.trim().toLowerCase();
    const list =
      this.selectedPillarSlug === '__unassigned__'
        ? this.preview.unassigned
        : this.selectedPillarPreview?.urls || [];
    if (!q) return list;
    return list.filter((u) => u.url.toLowerCase().includes(q));
  }

  get selectedUrl(): OrganizeUrlAssignment | null {
    if (!this.selectedUrlId || !this.preview) return null;
    for (const pillar of this.preview.pillars) {
      const hit = pillar.urls.find((u) => u.urlId === this.selectedUrlId);
      if (hit) return hit;
    }
    return this.preview.unassigned.find((u) => u.urlId === this.selectedUrlId) || null;
  }

  get filteredTopicPickerOptions(): TopicPickerOption[] {
    const q = this.addPillarSearch.trim().toLowerCase();
    const existing = new Set(this.pillars.map((p) => p.topicSlug));
    return this.topicPickerOptions
      .filter((t) => !existing.has(t.slug))
      .filter(
        (t) =>
          !q ||
          t.label.toLowerCase().includes(q) ||
          t.slug.includes(q) ||
          t.parentLabel.toLowerCase().includes(q)
      )
      .slice(0, 30);
  }

  resetWorkspace(): void {
    this.status = null;
    this.preview = null;
    this.pillars = [];
    this.pins = [];
    this.selectedPillarSlug = '';
    this.selectedUrlId = null;
    this.error = null;
    this.modal = 'none';
    this.proposalMeta = null;
    this.competitorInspiration = null;
    this.competitorInspirationError = null;
    this.expandedCompetitorBuckets = new Set();
  }

  loadStatus(): void {
    this.loading = true;
    this.error = null;
    this.clustersOrganize.getStatus(this.domain).subscribe({
      next: (res) => {
        this.loading = false;
        this.status = res.data;
        if (!res.data.ready) return;
        // K1: a domain with a committed plan opens ON that plan. It used to
        // open on a modal asking "suggestions or current?" over an empty
        // screen -- a question the screen could answer by rendering, and one
        // whose own copy admitted it already knew the answer ("This domain
        // already has plan clusters"). Cancel left you on a blank page.
        // Starting from suggestions is still available, as an action on the
        // organisation rather than a gate in front of it.
        if (res.data.hasCommittedPlan) {
          this.loadFromCommittedPlan();
        } else {
          this.loadProposal();
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || err?.message || 'Failed to load status';
      }
    });
  }

  loadProposal(): void {
    this.loading = true;
    this.clustersOrganize.getProposal(this.domain).subscribe({
      next: (res) => {
        this.loading = false;
        this.proposalMeta = res.data.meta || null;
        this.pillars = res.data.pillars.map((p) => ({
          topicSlug: p.topicSlug,
          label: p.label,
          tempId: this.clustersOrganize.newTempId()
        }));
        this.pins = [];
        this.modal = 'none';
        this.queuePreview();
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || err?.message || 'Failed to load proposal';
      }
    });
  }

  loadFromCommittedPlan(): void {
    this.loading = true;
    // lite=1: skip per-topic getPlanTopicCandidateRows (full-site aggregate × N topics).
    // Edit only needs pillar labels + membership urlIds for pins → preview.
    this.planClusters.getClusters(this.domain, { lite: true }).subscribe({
      next: (res) => {
        this.loading = false;
        const topics = res.data?.topics || [];
        this.pillars = this.clustersOrganize.pillarsFromPlanTopics(topics);
        this.pins = this.clustersOrganize.pinsFromPlanTopics(topics);
        this.modal = 'none';
        this.queuePreview();
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || err?.message || 'Failed to load plan';
      }
    });
  }

  queuePreview(): void {
    this.previewRequest$.next();
  }

  loadCompetitorInspiration(): void {
    if (!this.domain) return;
    this.competitorInspirationLoading = true;
    this.competitorInspirationError = null;
    this.clustersOrganize
      .getCompetitorInspiration(
        this.domain,
        this.pillars.map((p) => p.topicSlug)
      )
      .subscribe({
        next: (res) => {
          this.competitorInspirationLoading = false;
          this.competitorInspiration = res.data;
          this.expandedCompetitorBuckets = new Set(
            (res.data.buckets || [])
              .filter((b) => b.topicCount > 1)
              .map((b) => b.bucketId)
          );
        },
        error: (err) => {
          this.competitorInspirationLoading = false;
          this.competitorInspirationError =
            err?.error?.message || err?.message || 'Could not load competitor topics';
        }
      });
  }

  similarHintsForPillar(topicSlug: string): OrganizePillarSimilarHint[] {
    const pillar = this.preview?.pillars.find((p) => p.topicSlug === topicSlug);
    return pillar?.similarTo || [];
  }

  openMergeFromHint(sourceSlug: string, targetSlug: string): void {
    if (!sourceSlug || !targetSlug || sourceSlug === targetSlug) return;
    this.mergeSourceSlug = sourceSlug;
    this.mergeTargetSlug = targetSlug;
    this.modal = 'merge';
  }

  addPillarFromCompetitor(topicSlug: string, label: string): void {
    if (this.pillars.length >= 12) return;
    const slug = topicSlug.trim().toLowerCase();
    if (!slug || this.pillars.some((p) => p.topicSlug === slug)) return;
    this.pillars = [
      ...this.pillars,
      {
        topicSlug: slug,
        label: label.trim() || this.labelFromSlug(slug),
        tempId: this.clustersOrganize.newTempId()
      }
    ];
    this.queuePreview();
  }

  addPillarFromBucket(bucket: CompetitorInspirationBucketRow): void {
    this.addPillarFromCompetitor(bucket.representativeTopicSlug, bucket.representativeLabel);
  }

  toggleCompetitorViewMode(): void {
    this.competitorViewMode = this.competitorViewMode === 'grouped' ? 'flat' : 'grouped';
  }

  isCompetitorBucketExpanded(bucketId: string): boolean {
    return this.expandedCompetitorBuckets.has(bucketId);
  }

  toggleCompetitorBucket(bucketId: string): void {
    const next = new Set(this.expandedCompetitorBuckets);
    if (next.has(bucketId)) next.delete(bucketId);
    else next.add(bucketId);
    this.expandedCompetitorBuckets = next;
  }

  bucketGroupingLabel(bucket: CompetitorInspirationBucketRow): string {
    const reasons = (bucket.groupingReasons || []).filter((r) => !r.match(/^\d+ related topics$/));
    if (reasons.length > 0) return reasons.slice(0, 2).join(' · ');
    if (bucket.topicCount > 1) return `${bucket.topicCount} related topics`;
    return '';
  }

  topCompetitorLabel(row: { competitors: Array<{ domain: string; pageCount: number | null }> }): string {
    let bestDomain = '';
    let bestCount = -1;
    for (const c of row.competitors) {
      const count = c.pageCount || 0;
      if (count > bestCount) {
        bestCount = count;
        bestDomain = c.domain;
      }
    }
    return bestDomain && bestCount > 0 ? `${bestDomain} · ${bestCount} URLs` : '';
  }

  competitorPageCountForDomain(
    row: { competitors: Array<{ domain: string; pageCount: number | null }> },
    domain: string
  ): string {
    const hit = row.competitors.find((c) => c.domain === domain);
    if (!hit || hit.pageCount === null) return '—';
    return String(hit.pageCount);
  }

  selectPillar(slug: string | '__unassigned__'): void {
    this.selectedPillarSlug = slug;
    this.selectedUrlId = null;
  }

  selectUrl(urlId: string): void {
    this.selectedUrlId = urlId;
  }

  openAddPillar(): void {
    this.addPillarSearch = '';
    this.domainTopicsService.getDomainTopics(this.domain, { compact: true }).subscribe({
      next: (res) => {
        const parents = res.data?.parentTopics || [];
        this.topicPickerOptions = buildTopicOptionsFromParents(parents, true);
        this.modal = 'addPillar';
      },
      error: () => {
        this.topicPickerOptions = [];
        this.modal = 'addPillar';
      }
    });
  }

  addPillarFromTopic(topic: TopicPickerOption): void {
    if (this.pillars.length >= 12) return;
    const slug = topic.slug.trim().toLowerCase();
    if (this.pillars.some((p) => p.topicSlug === slug)) return;
    this.pillars = [
      ...this.pillars,
      {
        topicSlug: slug,
        label: topic.label,
        tempId: this.clustersOrganize.newTempId()
      }
    ];
    this.modal = 'none';
    this.queuePreview();
  }

  removePillar(slug: string): void {
    this.pillars = this.pillars.filter((p) => p.topicSlug !== slug);
    this.pins = this.pins.filter((p) => p.topicSlug !== slug);
    if (this.selectedPillarSlug === slug) {
      this.selectedPillarSlug = this.pillars[0]?.topicSlug || '__unassigned__';
    }
    this.queuePreview();
  }

  openMerge(): void {
    if (this.pillars.length < 2) return;
    this.mergeSourceSlug = this.pillars[1]?.topicSlug || '';
    this.mergeTargetSlug = this.pillars[0]?.topicSlug || '';
    this.modal = 'merge';
  }

  confirmMerge(): void {
    const source = this.mergeSourceSlug;
    const target = this.mergeTargetSlug;
    if (!source || !target || source === target) return;
    this.pillars = this.pillars.filter((p) => p.topicSlug !== source);
    this.pins = this.pins.map((p) => (p.topicSlug === source ? { ...p, topicSlug: target } : p));
    this.modal = 'none';
    this.queuePreview();
  }

  openSplit(slug: string): void {
    this.splitSourceSlug = slug;
    this.splitMode = 'pathPrefix';
    this.splitPathPrefix = '';
    this.splitChildSlug = '';
    this.splitNewLabel = '';
    this.splitNewSlug = '';
    this.splitManualUrlIds = [];
    this.splitChildOptions = [];
    this.clustersOrganize.getChildSlugs(this.domain, slug).subscribe({
      next: (res) => {
        this.splitChildOptions = res.data?.childSlugs || [];
      }
    });
    this.modal = 'split';
  }

  confirmSplit(): void {
    const source = this.splitSourceSlug;
    const newSlug = (this.splitNewSlug || this.slugify(this.splitNewLabel)).trim().toLowerCase();
    const newLabel = (this.splitNewLabel || this.labelFromSlug(newSlug)).trim();
    if (!source || !newSlug) return;
    if (!this.pillars.some((p) => p.topicSlug === newSlug)) {
      this.pillars = [...this.pillars, { topicSlug: newSlug, label: newLabel, tempId: this.clustersOrganize.newTempId() }];
    }

    const sourceUrls = this.preview?.pillars.find((p) => p.topicSlug === source)?.urls || [];
    const urlIdsToPin: string[] = [];

    if (this.splitMode === 'manual') {
      urlIdsToPin.push(...this.splitManualUrlIds);
    } else if (this.splitMode === 'pathPrefix') {
      const prefix = this.splitPathPrefix.trim().toLowerCase();
      for (const u of sourceUrls) {
        try {
          const path = new URL(u.url).pathname.toLowerCase();
          if (prefix && path.includes(prefix)) urlIdsToPin.push(u.urlId);
        } catch {
          // ignore
        }
      }
    } else if (this.splitMode === 'childSlug') {
      const child = this.splitChildSlug.trim().toLowerCase();
      for (const u of sourceUrls) {
        const reasonHit = u.reasons.some((r) => r.label.toLowerCase().includes(child));
        if (reasonHit) urlIdsToPin.push(u.urlId);
      }
    }

    for (const urlId of urlIdsToPin) {
      const idx = this.pins.findIndex((p) => p.urlId === urlId);
      if (idx >= 0) this.pins[idx] = { urlId, topicSlug: newSlug };
      else this.pins.push({ urlId, topicSlug: newSlug });
    }

    this.modal = 'none';
    this.queuePreview();
  }

  toggleManualSplitUrl(urlId: string): void {
    if (this.splitManualUrlIds.includes(urlId)) {
      this.splitManualUrlIds = this.splitManualUrlIds.filter((id) => id !== urlId);
    } else {
      this.splitManualUrlIds = [...this.splitManualUrlIds, urlId];
    }
  }

  moveUrlToPillar(urlId: string, topicSlug: string): void {
    const idx = this.pins.findIndex((p) => p.urlId === urlId);
    if (idx >= 0) this.pins[idx] = { urlId, topicSlug };
    else this.pins.push({ urlId, topicSlug });
    this.queuePreview();
  }

  unpinUrl(urlId: string): void {
    this.pins = this.pins.filter((p) => p.urlId !== urlId);
    this.queuePreview();
  }

  openApply(): void {
    this.applyMode = this.status?.hasCommittedPlan ? 'replace' : 'replace';
    this.modal = 'apply';
  }

  confirmApply(): void {
    if (!this.domain || this.pillars.length === 0) return;
    this.applying = true;
    this.clustersOrganize
      .apply(
        this.domain,
        this.pillars.map((p) => ({ topicSlug: p.topicSlug, label: p.label })),
        this.pins,
        this.applyMode
      )
      .subscribe({
        next: (res) => {
          this.applying = false;
          this.modal = 'none';
          const count = res.data?.appliedUrlCount ?? 0;
          this.notifications.success(`Organization applied — ${count} URL assignments saved.`);
          this.loadStatus();
        },
        error: (err) => {
          this.applying = false;
          this.notifications.error(err?.error?.message || err?.message || 'Apply failed');
        }
      });
  }

  discardDraft(): void {
    this.resetWorkspace();
    if (this.domain) this.loadStatus();
  }

  goToSetup(): void {
    this.router.navigate(['/setup']);
  }

  goToInventory(): void {
    this.router.navigate(['/inventory']);
  }

  closeModal(): void {
    this.modal = 'none';
  }

  slugify(label: string): string {
    return label.trim().toLowerCase().replace(/[_\s]+/g, '-').replace(/-+/g, '-');
  }

  labelFromSlug(slug: string): string {
    return slug.split('-').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  topicKindTagClass(topic: TopicPickerOption): string {
    return topicKindTagClassForOption(topic);
  }

  formatScore(score: number): string {
    return `${Math.round(score * 100)}%`;
  }

  formatAuthorityScore(score: number): string {
    return Number.isFinite(score) ? `${Math.round(score)}` : '—';
  }

  urlCountForPillar(topicSlug: string): number {
    return this.preview?.pillars.find((p) => p.topicSlug === topicSlug)?.urlCount ?? 0;
  }

  subtopicsForPillar(topicSlug: string) {
    const pillar = this.preview?.pillars.find((p) => p.topicSlug === topicSlug);
    if (!pillar || pillar.composition.mode !== 'distinct_subtopics') return [];
    return pillar.composition.subtopics.slice(0, 4);
  }

  sourcePillarUrlsForSplit(): OrganizeUrlAssignment[] {
    return this.preview?.pillars.find((p) => p.topicSlug === this.splitSourceSlug)?.urls || [];
  }

  trackPillar(_: number, p: OrganizePillarDraft): string {
    return p.tempId;
  }

  trackUrl(_: number, u: OrganizeUrlAssignment): string {
    return u.urlId;
  }
}
